/**
 * PartiteGiornata — le partite della giornata in tutte le competizioni, nel
 * pannello "Calcola giornata" (Andrea 2026-09-15). A giornata calcolata
 * presidente e admin correggono da qui a mano il risultato di una partita
 * (✎); sotto le partite corrette c'e' l'avviso con motivo e risultato del
 * calcolo.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  type PartitaItem,
  useCompetizioniStagione,
  usePartiteGiornata,
} from "../../hooks/useFase5";
import { AvvisoRettifica, RettificaModal } from "../partita/RettificaRisultato";
import { fmtPunti } from "../partita/rettifica";

const ORDINE_TIPO: Record<string, number> = {
  campionato: 0,
  champions_league: 1,
  supercoppa: 2,
};

interface PartiteGiornataProps {
  giornataId: string;
  stagioneId: string | null;
  /** Solo a giornata calcolata il risultato si corregge a mano. */
  calcolata: boolean;
  /** Presidente o admin della lega. */
  puoModificare: boolean;
  nomeSquadra: (id: string) => string;
}

export function PartiteGiornata({
  giornataId,
  stagioneId,
  calcolata,
  puoModificare,
  nomeSquadra,
}: PartiteGiornataProps) {
  const { data: partite } = usePartiteGiornata(giornataId);
  const { data: competizioni } = useCompetizioniStagione(stagioneId);
  const [inModifica, setInModifica] = useState<PartitaItem | null>(null);

  if (!partite || partite.length === 0) return null;

  const competizione = (id: string) => competizioni?.find((c) => c.id === id);
  const ordine = (id: string) => ORDINE_TIPO[competizione(id)?.tipo ?? ""] ?? 9;
  const gruppi = new Map<string, PartitaItem[]>();
  for (const p of partite) {
    gruppi.set(p.competizione_id, [...(gruppi.get(p.competizione_id) ?? []), p]);
  }
  const elenco = [...gruppi.entries()].sort(([a], [b]) => ordine(a) - ordine(b));
  const modificate = partite.filter((p) => p.rettifica).length;

  return (
    <div className="card p-0 overflow-hidden">
      <div className="bg-primary-deep/40 px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center justify-between gap-2">
        <span>Partite della giornata</span>
        {modificate > 0 && (
          <span
            className="shrink-0 whitespace-nowrap normal-case tracking-normal font-normal text-warning"
            title={`${modificate} ${modificate === 1 ? "risultato modificato" : "risultati modificati"} a mano`}
          >
            ✎ {modificate} a mano
          </span>
        )}
      </div>
      {elenco.map(([competizioneId, lista]) => (
        <div key={competizioneId}>
          <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-accent">
            {competizione(competizioneId)?.nome ?? "Competizione"}
          </div>
          {lista.map((p) => {
            const giocata = p.punteggio_casa !== null;
            return (
              <div
                key={p.id}
                className="px-2 sm:px-4 py-2 border-b border-primary/10 last:border-b-0 space-y-1.5"
              >
                <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <Link
                    to={`/partita/${p.id}`}
                    className="flex-1 min-w-0 flex items-center gap-1 sm:gap-2 hover:text-accent transition"
                  >
                    <span className="flex-1 min-w-0 font-bold truncate text-right">
                      {nomeSquadra(p.squadra_casa_id)}
                    </span>
                    <span className="w-14 sm:w-16 shrink-0 text-center font-mono font-bold">
                      {giocata ? `${p.gol_casa} - ${p.gol_trasferta}` : "vs"}
                    </span>
                    <span className="flex-1 min-w-0 font-bold truncate">
                      {nomeSquadra(p.squadra_trasferta_id)}
                    </span>
                  </Link>
                  {/* Andrea 2026-09-24: matita in colonnina fissa, non piu'
                      accanto al risultato. */}
                  <span
                    className="w-3 shrink-0 text-center text-warning text-[10px]"
                    title={p.rettifica ? "Risultato modificato manualmente" : undefined}
                  >
                    {p.rettifica ? "✎" : ""}
                  </span>
                  <span className="hidden sm:block w-24 shrink-0 text-right font-mono text-[10px] text-fg-muted">
                    {giocata
                      ? `${fmtPunti(p.punteggio_casa)} / ${fmtPunti(p.punteggio_trasferta)}`
                      : ""}
                  </span>
                  {calcolata && puoModificare && (
                    <button
                      type="button"
                      onClick={() => setInModifica(p)}
                      className="shrink-0 px-2 py-1 rounded border border-warning/40 text-warning text-[11px] hover:bg-warning/10 transition"
                      title="Modifica a mano il risultato"
                      aria-label={`Modifica il risultato di ${nomeSquadra(p.squadra_casa_id)} - ${nomeSquadra(p.squadra_trasferta_id)}`}
                    >
                      ✎
                    </button>
                  )}
                </div>
                {p.rettifica && <AvvisoRettifica rettifica={p.rettifica} />}
              </div>
            );
          })}
        </div>
      ))}
      {inModifica && (
        <RettificaModal
          partita={inModifica}
          nomeCasa={nomeSquadra(inModifica.squadra_casa_id)}
          nomeTrasferta={nomeSquadra(inModifica.squadra_trasferta_id)}
          stagioneId={stagioneId}
          onClose={() => setInModifica(null)}
        />
      )}
    </div>
  );
}
